/** @jsxImportSource @emotion/react */
'use memo'

import React from 'react'
import css from '@emotion/css/macro'
import { CheckOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { font, weightBold, paragraphMedium } from 'src/components/css'
import { ProfilePlayer } from '../../types'
import ProfilePlayerStatsRow from '../ProfilePlayerStatsRow'

const PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'
const AVATAR_SIZE = 32

interface PlayerSwitchRowProps {
  player: ProfilePlayer
  isSelected: boolean
  onSelect: () => void
}

/**
 * A clickable row in the player-switch dropdown. Shows avatar, name, the
 * player's stat chips (age / weight / height — same as the header, compact,
 * each hidden when missing), and a circled check when selected.
 */
const PlayerSwitchRow: React.FC<PlayerSwitchRowProps> = ({
  player,
  isSelected,
  onSelect,
}) => {
  const photoSrc = getImageThumbnailId(player) || PLACEHOLDER
  const fullName =
    `${player.name_first ?? ''} ${player.name_last ?? ''}`.trim() || 'Player'

  return (
    <div
      css={[rowStyle, isSelected && selectedRowStyle]}
      role='button'
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={fullName}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
    >
      <img
        src={photoSrc}
        alt={fullName}
        css={avatarStyle}
        onError={(e) => {
          const target = e.target as HTMLImageElement
          target.src = PLACEHOLDER
        }}
      />

      <div css={middleColStyle}>
        <span css={[nameStyle, isSelected && selectedNameStyle]}>
          {fullName}
        </span>
        <ProfilePlayerStatsRow player={player} compact />
      </div>

      {isSelected && (
        <CheckOutlined data-testid='player-switch-check' css={checkStyle} />
      )}
    </div>
  )
}

export default PlayerSwitchRow

// ─── Styles ──────────────────────────────────────────────────────────────────

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  /* Full-width band: highlight runs edge to edge (the scrollbar overlays the
     right edge). No border-radius so hover/selected reads as a flush row. The
     inter-row spacing lives in this padding (not the divider's margin) so the
     selected/hover band fills the whole row slot evenly — no narrow gap. */
  padding: 9px 12px;
  cursor: pointer;
  background: none;
  border: none;
  text-align: left;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${colors.SECONDARY_400};
  }
`

// Selected row: same SECONDARY_400 fill as hover so the two read consistently.
const selectedRowStyle = css`
  background-color: ${colors.SECONDARY_400};
`

const selectedNameStyle = css`
  color: ${colors.PRIMARY};
`

const avatarStyle = css`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`

const middleColStyle = css`
  display: flex;
  flex-direction: column;
  /* Breathing room between the name (line 1) and the stat chips (line 2). */
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const nameStyle = css`
  ${font.body}
  ${weightBold}
  ${paragraphMedium}
  /* Tighter than the preset's 145% — font-size (16px) + 2px. */
  line-height: 18px;
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const checkStyle = css`
  color: ${colors.PRIMARY};
  font-size: 16px;
  flex-shrink: 0;
  margin: 0 5px;
`
